import { Link } from 'react-router-dom';
import { getCurrentWeek } from '@/lib/datetime';
import { dayStartKey, getDayTime } from '@/lib/schedule';
import type { ClassTimes } from '@/types';

export interface ScheduleStudent {
  id: string;
  name: string;
  class_days: string[];
  class_times: ClassTimes | null;
  class_start_time: string | null;
  class_end_time: string | null;
}

interface WeekScheduleCardProps {
  students: ScheduleStudent[];
}

export default function WeekScheduleCard({ students }: WeekScheduleCardProps) {
  const week = getCurrentWeek();

  const perDay = week.map((w) => ({
    ...w,
    lessons: students
      .filter((s) => (s.class_days ?? []).includes(w.day))
      .sort((a, b) => dayStartKey(a, w.day).localeCompare(dayStartKey(b, w.day))),
  }));

  const totalThisWeek = perDay.reduce((acc, d) => acc + d.lessons.length, 0);
  const todayCount = perDay.find((d) => d.isToday)?.lessons.length ?? 0;

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-calendar-schedule-line"></i>
          </span>
          <h2 className="text-base font-bold text-foreground-950">이번 주 수업일정</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-secondary-100 px-3 py-1 font-semibold text-secondary-900">
            이번 주 {totalThisWeek}건
          </span>
          <span className="rounded-full bg-accent-100 px-3 py-1 font-semibold text-accent-800">
            오늘 {todayCount}건
          </span>
        </div>
      </div>

      {students.length === 0 || totalThisWeek === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-10">
          <i className="ri-calendar-line text-2xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">이번 주에 예정된 수업이 없어요.</p>
          <Link
            to="/students/new"
            className="mt-1 flex items-center gap-1 rounded-md bg-primary-500 px-3.5 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            학생 등록하기
          </Link>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-7">
          {perDay.map((d) => (
            <div
              key={d.day}
              className={`flex min-h-[132px] flex-col rounded-lg border p-3 ${
                d.isToday
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-background-200 bg-background-100'
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold ${
                    d.isToday
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-700'
                  }`}
                >
                  {d.day}
                </span>
                {d.isToday && (
                  <span className="text-[11px] font-semibold text-primary-700">오늘</span>
                )}
              </div>

              <div className="mt-2 flex flex-1 flex-col gap-1.5">
                {d.lessons.length === 0 ? (
                  <p className="mt-1 text-[11px] text-foreground-400">수업 없음</p>
                ) : (
                  d.lessons.map((s) => {
                    const t = getDayTime(s, d.day);
                    return (
                      <Link
                        key={`${d.day}-${s.id}`}
                        to={`/students/${s.id}`}
                        className="group rounded-md border border-background-200 bg-background-50 px-2 py-1.5 transition hover:border-primary-300"
                      >
                        <p className="truncate text-xs font-semibold text-foreground-900 group-hover:text-primary-700">
                          {s.name}
                        </p>
                        {t.start && (
                          <p className="mt-0.5 flex items-center gap-0.5 text-[11px] text-foreground-500">
                            <i className="ri-time-line"></i>
                            {t.start}
                            {t.end ? `~${t.end}` : ''}
                          </p>
                        )}
                      </Link>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}